<template>
  <modal-default
    ref="modal"
    title="Merge Leads"
    :short-circuit-close="true"
    is-wide
  >
    <app-loading-section :loading="isLoading">
      <template v-if="currentLead && duplicateLead">
        <h3 class="merge_lead_modal--heading mt-0">Keep Which Lead?</h3>
        <p class="merge_lead_modal--instructions">Select the lead you want to keep. The other lead will be deleted.</p>

        <div class="merge_lead_modal--section">
          <div class="keepRow">
            <div class="keepRow--spacer"></div>
            <div class="keepRow--column">
              <b-radio v-model="keepLead" :native-value="currentLeadId" class="mr-0">
                <span>Keep this lead?</span>
              </b-radio>
            </div>
            <div class="keepRow--column">
              <b-radio v-model="keepLead" :native-value="duplicateLeadId" class="mr-0">
                <span>Keep this lead?</span>
              </b-radio>
            </div>
          </div>
        </div>

        <h3 class="merge_lead_modal--heading">Lead Details</h3>

        <div class="merge_lead_modal--section">
          <MergeLeadRow
            heading="Status"
            :current-label="currentStatusLabel"
            :duplicate-label="duplicateStatusLabel"
            :current-value="currentLead.active_status.status_id"
            :duplicate-value="duplicateLead.active_status.status_id"
          />
          <MergeLeadRow
            heading="Address"
            :current-value="currentLead.property.line_1"
            :duplicate-value="duplicateLead.property.line_1"
          />
        </div>

        <h3 class="merge_lead_modal--heading">Primary Contact Info</h3>

        <div class="merge_lead_modal--section">
          <MergeLeadRow
            heading="First Name"
            field-name="first_name"
            :current-value="(currentLead.contact) ? currentLead.contact.first_name : ''"
            :duplicate-value="(duplicateLead.contact) ? duplicateLead.contact.first_name : ''"
            include-radio
            @change="changeInput"
          />
          <MergeLeadRow
            heading="Last Name"
            field-name="last_name"
            :current-value="(currentLead.contact) ? currentLead.contact.last_name : ''"
            :duplicate-value="(duplicateLead.contact) ? duplicateLead.contact.last_name : ''"
            include-radio
            @change="changeInput"
          />
          <MergeLeadRow
            heading="Email Address"
            field-name="email_address"
            :current-value="(currentLead.contact) ? currentLead.contact.email_address : ''"
            :duplicate-value="(duplicateLead.contact) ? duplicateLead.contact.email_address : ''"
            include-radio
            @change="changeInput"
          />
        </div>

        <h3 class="merge_lead_modal--heading">Primary Contact Numbers</h3>
        <p class="merge_lead_modal--instructions">Check the boxes of the numbers you want to keep.</p>

        <div class="merge_lead_modal--section">
          <MergeContactPhones
            ref="contactPhones"
            :current-value="(currentLead.contact) ? currentLead.contact.phone_1 : null"
            :current-phones="phoneNumbers.currentPhones"
            :duplicate-phones="phoneNumbers.duplicatePhones"
          />
        </div>
      </template>
    </app-loading-section>

    <template v-slot:footer>
      <button-outlined :disabled="isButtonLoading" @click="close">
        cancel
      </button-outlined>

      <button-default :loading="isButtonLoading" @click="merge">
        Merge
      </button-default>
    </template>
  </modal-default>
</template>

<script>
import {defineComponent} from 'vue'
import api from "../../api";
import MergeLeadRow from "./components/MergeLeadRow.vue";
import MergeContactPhones from "./components/MergeContactPhones.vue";
import {destroyModal} from "../../services/modals";

export default defineComponent({
  name: "MergeDuplicateLeadModal",
  components: {MergeContactPhones, MergeLeadRow},
  props: {
    currentLeadId: {
      type: String | Number,
      required: true,
    },
    duplicateLeadId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isButtonLoading: false,
      keepLead: this.currentLeadId,
      currentLead: null,
      duplicateLead: null,
      payload: {
        duplicated_record_id: null,
        duplicate_contact_id: null,
        primary_record_id: null,
        primary_contact_id: null,
        merge_map: {
          contact_fields: {
            first_name: null,
            last_name: null,
            email_address: null,
            phone_1: null,
            phone_2: null,
            phone_3: null,
            phone_4: null,
            phone_5: null,
            phone_6: null,
            phone_7: null,
            phone_8: null,
            phone_9: null,
            phone_10: null,
          },
        },
      }
    }
  },
  mounted() {
    this.$refs.modal.open();
    this.getLeads()
  },
  computed: {
    statuses() {
      return this.$store.getters.statuses
    },
    currentStatusLabel() {
      const status = this.statuses.find(status => {
        return status.id === this.currentLead.active_status.status_id
      })

      return (status) ? status.label : null
    },
    duplicateStatusLabel() {
      const status = this.statuses.find(status => {
        return status.id === this.duplicateLead.active_status.status_id
      })

      return (status) ? status.label : null
    },
    phoneNumbers() {
      let currentPhones = []
      let duplicatePhones = []
      const keys = ['phone_1', 'phone_2', 'phone_3', 'phone_4', 'phone_5', 'phone_6', 'phone_7', 'phone_8', 'phone_9', 'phone_10']

      if (this.currentLead && this.currentLead.contact) {
        keys.forEach(key => {
          if (this.currentLead.contact[key] && this.currentLead.contact[key] !== null) {
            currentPhones.push({
              field: key,
              value: this.currentLead.contact[key]
            })
          }
        })
      }

      if (this.duplicateLead && this.duplicateLead.contact) {
        keys.forEach(key => {
          if (this.duplicateLead.contact[key] && this.duplicateLead.contact[key] !== null) {
            duplicatePhones.push({
              field: key,
              value: this.duplicateLead.contact[key]
            })
          }
        })
      }

      return {
        currentPhones: currentPhones,
        duplicatePhones: duplicatePhones
      }
    },
  },
  methods: {
    changeInput(data) {
      if (this.payload.merge_map.contact_fields.hasOwnProperty(data.field)) {
        this.payload.merge_map.contact_fields[data.field] = data.value
      }
    },
    close() {
      destroyModal(this.$refs.modal)
      this.$refs.modal.close()
    },
    merge() {
      console.log('Here');
      if (! this.$refs.contactPhones.isValid()) {
        this.$buefy.toast.open({
          message: 'You can only select up to 10 phone numbers',
          type: 'is-danger'
        })
        return
      }

      this.payload = {
        duplicated_record_id: (this.keepLead === this.currentLeadId) ? this.duplicateLeadId : this.currentLeadId,
        duplicate_contact_id: (this.keepLead === this.currentLeadId) ?  (this.duplicateLead.contact) ? this.duplicateLead.contact.id : null : (this.currentLead.contact) ? this.currentLead.contact.id : null,
        primary_record_id: this.keepLead,
        primary_contact_id: (this.keepLead === this.currentLeadId) ? (this.currentLead.contact) ? this.currentLead.contact.id : null : (this.duplicateLead.contact) ? this.duplicateLead.contact.id : null,
        merge_map: {
          contact_fields: {
            ...this.payload.merge_map.contact_fields,
            ...this.$refs.contactPhones.emitValues()
          },
        },
      }

      this.isButtonLoading = true
      api.contacts.mergeDuplicate(this.payload)
        .then(res => {
          this.$emit('reloadList')
          this.close()

          this.$buefy.toast.open({
            message: 'Merge Completed',
            type: 'is-success'
          })
        })
        .finally(() => { this.isButtonLoading = false })
    },
    getLeads() {
      this.isLoading = true
      Promise.all([
        api.records.get(this.currentLeadId),
        api.records.get(this.duplicateLeadId),
      ]).then(([currentLeadRes, duplicateLeadRes]) => {
        this.currentLead = currentLeadRes.data
        this.duplicateLead = duplicateLeadRes.data
      }).finally(() => { this.isLoading = false })
    }
  },
})
</script>

<style scoped lang="scss">
@import "../../styles/variables/variables";

.keepRow {
  display: flex;
  justify-content: space-between;

  &--spacer {
    width: 26%;
  }

  &--column {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: calc(37% - 24px);
    padding: 0 .25rem 6px;
    border-bottom: 1px solid $border;
  }
}

.merge_lead_modal {
  &--heading {
    margin: 1.5rem 0 0.75rem;
    font-weight: bold;
  }

  &--instructions {
    margin: -0.5rem 0 0.75rem;
    font-size: 0.75rem;
  }

  &--section {
    padding-left: 14px;
  }
}

.merge_duplicate {
  &--columns {
    width: 100%;
    margin-top: 1rem;
    display: flex;
    justify-content: space-between;
  }

  &--column {
    width: calc(33.3333% - 8px);
  }
}
</style>
